import { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { themes } from "./theme.js";
import GlobalStyles from "./components/GlobalStyles.jsx";
import Header from "./components/Header.jsx";
import Footer from "./components/Footer.jsx";
import HomePage from "./components/HomePage.jsx";
import WorkPage from "./components/WorkPage.jsx";
import ContactPage from "./components/ContactPage.jsx";
import PathPage from "./components/PathPage.jsx";
import ProjectDetail from "./components/ProjectDetail.jsx";
import CertificatesPage from "./components/CertificatesPage.jsx";
import CertificateDetail from "./components/CertificateDetail.jsx";
import Error404 from "./components/Error404.jsx";
import ExperiencePage from "./components/ExperiencePage.jsx";
import BlogsPage from "./components/BlogsPage.jsx";
import BlogPage from "./components/BlogPage.jsx";
import { defaultLanguage, getTranslations } from "./i18n.js";

const getSystemDark = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const getSavedThemeMode = () =>
  typeof localStorage === "undefined"
    ? "system"
    : localStorage.getItem("themeMode") || "system";

const getSavedLanguage = () =>
  typeof localStorage === "undefined"
    ? defaultLanguage
    : localStorage.getItem("language") || defaultLanguage;

export default function App() {
  const [themeMode, setThemeMode] = useState(getSavedThemeMode);
  const [language, setLanguage] = useState(getSavedLanguage);
  const [systemDark, setSystemDark] = useState(getSystemDark);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = (e) => setSystemDark(e.matches);

    media.addEventListener("change", listener);

    return () => media.removeEventListener("change", listener);
  }, []);

  useEffect(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("themeMode", themeMode);
    }
  }, [themeMode]);

  useEffect(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("language", language);
    }
  }, [language]);

  const isDark = themeMode === "system" ? systemDark : themeMode === "dark";
  const t = isDark ? themes.dark : themes.light;
  const copy = getTranslations(language);

  return (
    <Router>
      <div
        className="alex-root"
        style={{
          background: t.bg,
          color: t.text,
          minHeight: "100vh",
          fontSize: 16,
          lineHeight: 1.7,
          margin: 0,
          padding: 0,
        }}
      >
        <GlobalStyles t={t} />

        <div style={{ maxWidth: 680, margin: "0 auto", padding: "48px 24px 80px" }}>
          <Header
            themeMode={themeMode}
            setThemeMode={setThemeMode}
            language={language}
            setLanguage={setLanguage}
            t={t}
            copy={copy}
          />

          <Routes>
            <Route path="/" element={<HomePage t={t} copy={copy} language={language} />} />
            <Route path="/study" element={<PathPage t={t} copy={copy} language={language} />} />
            <Route path="/certificates" element={<CertificatesPage t={t} copy={copy} language={language} />} />
            <Route path="/certificates/:certificateId" element={<CertificateDetail t={t} copy={copy} language={language} />} />
            <Route path="/projects" element={<WorkPage t={t} copy={copy} language={language} />} />
            <Route path="/experience" element={<ExperiencePage t={t} copy={copy} language={language} />} />
            <Route path="/projects/:projectId" element={<ProjectDetail t={t} copy={copy} language={language} />} />
            <Route path="/contact" element={<ContactPage t={t} copy={copy} />} />
            <Route path="/blog" element={<BlogsPage t={t} copy={copy} language={language} />} />
            <Route path="/blog/:blogId" element={<BlogPage t={t} copy={copy} language={language} />} />
            <Route path="*" element={<Error404 t={t} copy={copy} />} />
          </Routes>

          <Footer t={t} />
        </div>
      </div>
    </Router>
  );
}
